import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Statistic, Descriptions, Table, InputNumber, Button, Popconfirm, message, Typography, Tag, Space } from 'antd';
import { ReloadOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import { MongoRef } from './MongoRef';

const { Text } = Typography;

export { SystemTab };

// counts key → Vietnamese label
const COUNT_LABELS = {
    users:           'Người dùng',
    classes:         'Lớp học',
    scores:          'Điểm số',
    attendance:      'Buổi điểm danh',
    tuitionRecords:  'Khoản thu học phí',
    tuitionPackages: 'Gói học phí',
    terms:           'Học kỳ',
    subjects:        'Môn học',
};

// role → display label + tag colour (mirrors StaffManagePage's ROLE_META, plus student/admin)
const ROLE_META = {
    student:    { label: 'Học sinh',    color: 'green'  },
    teacher:    { label: 'Giáo viên',   color: 'blue'   },
    manager:    { label: 'Quản lý',     color: 'gold'   },
    headmaster: { label: 'Hiệu trưởng', color: 'purple' },
    admin:      { label: 'Admin',       color: 'red'    },
};

function SystemTab() {
    const fetchWrapper = useFetchWrapper();

    const [stats, setStats]               = useState(null);
    const [statsLoading, setStatsLoading]  = useState(false);

    const [counters, setCounters]             = useState([]);
    const [countersLoading, setCountersLoading] = useState(false);
    const [editing, setEditing]               = useState({}); // { [_id]: pendingSeqValue }
    const [savingId, setSavingId]             = useState(null);

    const [backupRunning, setBackupRunning] = useState(false);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/admin/stats', null, null);
            const json = await res.json();
            if (json.status === 'Success') setStats(json.message);
            else message.error(typeof json.message === 'string' ? json.message : 'Không tải được thống kê hệ thống');
        } catch (e) {
            message.error('Không tải được thống kê hệ thống');
        }
        setStatsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCounters = useCallback(async () => {
        setCountersLoading(true);
        try {
            const res  = await fetchWrapper.get('/api/admin/counters', null, null);
            const json = await res.json();
            if (json.status === 'Success') setCounters(Array.isArray(json.message) ? json.message : []);
            else message.error(typeof json.message === 'string' ? json.message : 'Không tải được bộ đếm ID');
        } catch (e) {
            message.error('Không tải được bộ đếm ID');
        }
        setCountersLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadStats(); loadCounters(); }, [loadStats, loadCounters]);

    function handleEditSeq(id, value) {
        setEditing(prev => ({ ...prev, [id]: value }));
    }

    async function handleSaveCounter(record) {
        const seq = editing[record._id] !== undefined ? editing[record._id] : record.seq;
        if (typeof seq !== 'number' || Number.isNaN(seq) || seq < 0) {
            message.error('Giá trị seq không hợp lệ');
            return;
        }
        setSavingId(record._id);
        try {
            const res  = await fetchWrapper.post(
                '/api/admin/counters', 'application/json', JSON.stringify({ id: record._id, seq })
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success(`Đã cập nhật bộ đếm "${record._id}" → ${seq}`);
                setEditing(prev => { const next = { ...prev }; delete next[record._id]; return next; });
                await loadCounters();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Cập nhật thất bại');
            }
        } catch (e) {
            message.error('Cập nhật thất bại');
        }
        setSavingId(null);
    }

    async function handleBackup() {
        setBackupRunning(true);
        try {
            const res  = await fetchWrapper.post('/api/admin/backup', null, null);
            const json = await res.json();
            if (json.status === 'Success') message.success(typeof json.message === 'string' ? json.message : 'Backup đã bắt đầu');
            else message.error(typeof json.message === 'string' ? json.message : 'Không thể khởi động backup');
        } catch (e) {
            message.error('Không thể khởi động backup');
        }
        setBackupRunning(false);
    }

    const counts       = stats?.counts || {};
    const usersByRole  = stats?.usersByRole || {};

    const counterColumns = [
        { title: 'ID bộ đếm', dataIndex: '_id', key: '_id', render: v => <Text code>{v}</Text> },
        { title: 'Mongo', key: 'mongo', width: 120, render: (_, r) => <MongoRef id={r._id} collection="counters" /> },
        { title: 'Giá trị hiện tại', dataIndex: 'seq', key: 'seq', width: 140 },
        {
            title: 'Sửa',
            key: 'edit',
            width: 240,
            render: (_, record) => {
                const pending = editing[record._id] !== undefined ? editing[record._id] : record.seq;
                return (
                    <Space>
                        <InputNumber
                            min={0}
                            value={pending}
                            onChange={v => handleEditSeq(record._id, v)}
                            style={{ width: 100 }}
                        />
                        <Popconfirm
                            title={`Đổi bộ đếm "${record._id}" thành ${pending}?`}
                            onConfirm={() => handleSaveCounter(record)}
                            okText="Lưu"
                            cancelText="Hủy"
                        >
                            <Button size="small" type="primary" loading={savingId === record._id}>Lưu</Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card
                title="Thống kê hệ thống"
                loading={statsLoading}
                extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadStats}>Làm mới</Button>}
            >
                <Row gutter={[16, 16]}>
                    {Object.keys(COUNT_LABELS).map(key => (
                        <Col xs={12} sm={8} md={6} key={key}>
                            <Statistic title={COUNT_LABELS[key]} value={counts[key] ?? 0} />
                        </Col>
                    ))}
                </Row>

                <div style={{ marginTop: 24 }}>
                    <Text strong>Người dùng theo vai trò</Text>
                    <div style={{ marginTop: 8 }}>
                        {Object.keys(usersByRole).length === 0 && <Text type="secondary">Không có dữ liệu</Text>}
                        {Object.entries(usersByRole).map(([role, n]) => {
                            const meta = ROLE_META[role] || { label: role, color: 'default' };
                            return (
                                <Tag color={meta.color} key={role} style={{ marginBottom: 4 }}>
                                    {meta.label}: {n}
                                </Tag>
                            );
                        })}
                    </div>
                </div>

                <Descriptions column={2} size="small" bordered style={{ marginTop: 24 }}>
                    <Descriptions.Item label="Dung lượng dữ liệu (DB size)">
                        {stats && stats.dbSizeMB != null ? `${stats.dbSizeMB} MB` : <Text type="secondary">Không có dữ liệu</Text>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Dung lượng lưu trữ (Storage size)">
                        {stats && stats.storageMB != null ? `${stats.storageMB} MB` : <Text type="secondary">Không có dữ liệu</Text>}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title="Bộ đếm ID (Counters)" loading={countersLoading}>
                <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                    Lưu ý: chỉnh sửa bộ đếm ảnh hưởng trực tiếp đến việc sinh mã ID tiếp theo (học sinh / giáo viên). Chỉ thay đổi khi chắc chắn.
                </Text>
                <Table
                    columns={counterColumns}
                    dataSource={counters.map(c => ({ ...c, key: c._id }))}
                    pagination={false}
                    size="small"
                />
            </Card>

            <Card title="Sao lưu dữ liệu (Backup)">
                <Text style={{ display: 'block', marginBottom: 12 }}>
                    Chạy sao lưu toàn bộ cơ sở dữ liệu ngay lập tức và tải lên Google Drive.
                </Text>
                <Popconfirm
                    title="Chạy backup ngay bây giờ?"
                    onConfirm={handleBackup}
                    okText="Chạy"
                    cancelText="Hủy"
                >
                    <Button type="primary" icon={<CloudUploadOutlined />} loading={backupRunning}>
                        Chạy backup ngay
                    </Button>
                </Popconfirm>
            </Card>
        </div>
    );
}
